/**
 * Сегодняшняя дата как состояние.
 *
 * Приложение на телефоне живёт неделями без перезапуска: его сворачивают, и
 * утром оно просыпается из фона, а не стартует заново. Дата, прочитанная один
 * раз при открытии экрана, продолжала жить и назавтра — задачи не переносились,
 * экран показывал вчера, напоминания считались по вчерашнему дню, пока
 * приложение не закрывали вручную.
 *
 * Сутки проверяются раз в минуту, пока приложение на экране, и сразу при
 * возвращении в него: в фоне таймеры телефона спят, и минутной проверки после
 * ночи пришлось бы ждать. Таймер «до полуночи» не годится: многочасовой таймер
 * не переживает ни сна устройства, ни перевода часов, а лишняя проверка раз в
 * минуту не стоит ничего.
 *
 * О возвращении сообщают и страница, и оболочка приложения. Событию страницы
 * на Android верить до конца нельзя — оболочка может держать её «видимой» и в
 * фоне, — поэтому на телефоне слушается и возвращение самого приложения.
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

import { type IsoDate, today as todayIso } from './datetime';

export function useToday(): IsoDate {
  const [date, setDate] = useState(todayIso);

  useEffect(() => {
    const check = () => setDate(todayIso());

    const timer = setInterval(check, 60_000);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    const resume = Capacitor.isNativePlatform() ? App.addListener('resume', check) : null;

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
      void resume?.then((subscription) => subscription.remove());
    };
  }, []);

  return date;
}

/**
 * Выбранный на экране день, который переезжает вместе с сутками.
 *
 * Экран, открытый на «сегодня», наутро должен показывать новое сегодня, а не
 * вчерашний день. Выбранный вручную другой день остаётся на месте: человек
 * смотрел на четверг — четверг и увидит.
 *
 * `onFollow` — экрану сдвинуть вслед то, что выводится из дня: полосу недели.
 * Возвращает выбранный день, его установку и сегодняшнюю дату.
 */
export function useSelectedDay(
  onFollow?: (today: IsoDate) => void,
): [IsoDate, Dispatch<SetStateAction<IsoDate>>, IsoDate] {
  const today = useToday();
  const [selected, setSelected] = useState(today);
  const [seenToday, setSeenToday] = useState(today);

  // Поправка прямо во время отрисовки, а не эффектом: иначе экран на один кадр
  // успевал показать вчерашний день с уже новыми данными.
  if (seenToday !== today) {
    setSeenToday(today);
    if (selected === seenToday) {
      setSelected(today);
      onFollow?.(today);
    }
  }

  return [selected, setSelected, today];
}
